import { IndexingStatusIcon } from "@/components/indexing-status/IndexingStatusIcon";
import { ContentMatchIndicator } from "@/pages/workspace/components/content-search/ContentMatchIndicator";
import {
  isActiveRevisionSearchIndexStale,
  Item,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { getLinkHostname } from "@/pages/workspace/utils/linkUrl";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { MoreHorizontal, Link2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn, formatBytes, formatDate, getFileTypeIconSrc } from "@/lib/utils";
import { UploadSource } from "@/components/upload/types/upload-item";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { useFileOperations } from "@/pages/workspace/hooks/useFileOperations";
import UserAvatar from "@/components/images/UserAvatar";
import { formatUsersName } from "@/utils/format";
import FileItemDropdownMenu from "./FileItemDropdownMenu";
import SubscribeButton from "@/components/buttons/SubscribeButton";
import SubscribeAsGroupButton from "@/components/buttons/SubscribeAsGroupButton";
import ManageGroupSubscriptionModal from "@/components/modals/ManageGroupSubscriptionModal";
import FavoriteButton from "@/components/buttons/FavoriteButton";
import { DocumentCategory } from "@/models/legislation/Legislation";
import useUserTokenStore from "@/stores/user-token.store";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import { Permission } from "@/lib/permissions/Permission.enum";
import { permissionMap } from "@/lib/permissions/permissionMap";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { useEffect, useRef, useState } from "react";
import { DocumentIndexingView } from "@/pages/workspace/utils/indexingProgress";
import {
  IndexingProgressLine,
  IndexingProgressTooltip,
} from "./IndexingProgress";

type FileGridProps = {
  sortedItems: Item[];
  /** Documents with an indexing run in flight, and where each is (LAR-486). */
  indexingDocuments?: ReadonlyMap<string, DocumentIndexingView>;
  selectedItems: string[];
};

const TruncatedTitle = ({ title }: { title: string }) => {
  const textRef = useRef<HTMLHeadingElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [title]);

  const content = (
    <h3 ref={textRef} className="truncate text-sm font-medium leading-tight">
      {title}
    </h3>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs wrap-break-word">
        {title}
      </TooltipContent>
    </Tooltip>
  );
};

const FileGrid = ({
  sortedItems,
  selectedItems,
  indexingDocuments,
}: FileGridProps) => {
  const { tenantUsers, textOnlyMatches } = useWorkspaceContext();
  const {
    workspaceId,
    favorites,
    subscriptions,
    groupSubscriptionEntityPaths,
    draggedItem,
    dropTarget,
    itemBusy,
    busyReason,
    openDropdownId,
    setOpenDropdownId,
    setBusy,
    clearBusy,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRowClick,
    canUserMoveItems,
  } = useFileOperations();
  const canMoveItems = canUserMoveItems();

  const hasCreateFeatureGroups = useUserTokenStore((s) =>
    s.permissions.includes(permissionMap[Permission.CreateFeatureGroups])
  );

  const [groupSubscriptionModalItem, setGroupSubscriptionModalItem] = useState<{
    entityPath: string;
    entityName: string;
    itemId: string;
    subscribeCategory: SubscriptionCategory;
  } | null>(null);

  const isValidDropTarget = (item: Item) => {
    return (
      draggedItem !== null &&
      item.type === ITEM_TYPE_FOLDER &&
      item.id === dropTarget
    );
  };
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {sortedItems.map((item) => {
          const isSelected = selectedItems.includes(item.id);
          const isDragging = draggedItem === item.id;
          const isDropTargetItem = isValidDropTarget(item);
          const isFavorite = favorites?.workspace?.includes(item.id) ?? false;
          const isDocument = item.type === ITEM_TYPE_DOCUMENT;
          const isLink = item.type === ITEM_TYPE_LINK;
          const textMatch = textOnlyMatches.get(item.id);
          const entityPath =
            item.type === ITEM_TYPE_FOLDER
              ? `workspace/${workspaceId}/folder/${item.id}`
              : `workspace/${workspaceId}/document/${item.id}`;

          const isSubscribed = subscriptions.some(
            (s: { entityPath: string }) => s.entityPath === entityPath
          );
          const isGroupSubscribed =
            groupSubscriptionEntityPaths?.has(entityPath) ?? false;
          const documentOwner = tenantUsers.find(
            (user) => user.id === item.userId
          );
          const indexing = indexingDocuments?.get(item.id);

          return (
            <div
              key={item.id}
              className={cn(
                "group relative flex cursor-pointer flex-col rounded-lg border bg-card p-6 transition-all duration-200 hover:shadow-md hover:border-primary/50",
                isSelected
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-border",
                isDragging ? "opacity-50" : "",
                dropTarget === item.id ? "bg-muted" : "",
                itemBusy[item.id] ? "opacity-50 pointer-events-none" : ""
              )}
              draggable={canMoveItems}
              onDragStart={(e) =>
                !itemBusy[item.id] && handleDragStart(e, item.id)
              }
              onDragOver={(e) =>
                !itemBusy[item.id] && handleDragOver(e, item.id)
              }
              onDragLeave={() => !itemBusy[item.id] && handleDragLeave()}
              onDrop={(e) =>
                draggedItem &&
                !itemBusy[item.id] &&
                handleDrop(e, item.id, draggedItem)
              }
              onClick={(e) => !itemBusy[item.id] && handleRowClick(item, e)}
              style={{
                backgroundImage:
                  !itemBusy[item.id] && isDropTargetItem
                    ? "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(59, 130, 246, 0.1) 5px, rgba(59, 130, 246, 0.1) 10px)"
                    : "none",
                boxShadow:
                  !itemBusy[item.id] && isDropTargetItem
                    ? "0 0 0 2px rgba(59, 130, 246, 0.5)"
                    : "none",
              }}
            >
              {itemBusy[item.id] && (
                <div className="absolute inset-0 z-20 flex items-center justify-center rounded-lg backdrop-blur-[1.5px] bg-background/50 dark:bg-background/60">
                  <div className="flex items-center gap-2">
                    <Spinner className="size-4" />
                    <span className="text-xs text-muted-foreground">
                      {busyReason[item.id]}
                    </span>
                  </div>
                </div>
              )}
              <div
                className={cn(
                  "absolute right-2 top-2 z-10 flex items-center gap-1 transition-opacity duration-200"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenu
                  open={openDropdownId === item.id}
                  onOpenChange={(open) => {
                    if (open) {
                      setOpenDropdownId(item.id);
                    } else {
                      setOpenDropdownId(null);
                    }
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn("size-7 transition-opacity duration-200")}
                    >
                      <div className="flex items-center justify-center">
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <FileItemDropdownMenu item={item} />
                </DropdownMenu>
                {/* Favorites and subscriptions address documents and folders; a link is neither. */}
                {!isLink && (
                  <>
                    <div
                      className={cn(
                        "flex flex-row group-hover:block",
                        !(
                          openDropdownId === item.id ||
                          isSubscribed ||
                          useSubscriptionOperationStore
                            .getState()
                            .isPending(entityPath)
                        ) && "hidden"
                      )}
                    >
                      <SubscribeButton
                        subscribeCategory={
                          item.type === ITEM_TYPE_FOLDER
                            ? SubscriptionCategory.Workspace
                            : SubscriptionCategory.WorkspaceDocument
                        }
                        entityPath={entityPath}
                        entityName={item.name}
                        source="workspace"
                        isSubscribed={isSubscribed}
                        className="size-6 hover:bg-muted/80"
                        onOperationStart={() => {
                          setBusy(item.id, "subscription");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>
                    <div
                      className={cn(
                        "flex flex-row group-hover:block",
                        !(openDropdownId === item.id || isGroupSubscribed) &&
                          hasCreateFeatureGroups &&
                          "hidden",
                        !hasCreateFeatureGroups &&
                          !isGroupSubscribed &&
                          "hidden"
                      )}
                    >
                      <SubscribeAsGroupButton
                        subscribeCategory={
                          item.type === ITEM_TYPE_FOLDER
                            ? SubscriptionCategory.Workspace
                            : SubscriptionCategory.WorkspaceDocument
                        }
                        entityPath={entityPath}
                        entityName={item.name}
                        source="workspace"
                        isGroupSubscribed={isGroupSubscribed}
                        className="size-6 hover:bg-muted/80"
                        open={
                          groupSubscriptionModalItem?.entityPath === entityPath
                        }
                        onOpenChange={(open) => {
                          if (!open) setGroupSubscriptionModalItem(null);
                        }}
                        onOpenRequest={() => {
                          setGroupSubscriptionModalItem({
                            entityPath,
                            entityName: item.name,
                            itemId: item.id,
                            subscribeCategory:
                              item.type === ITEM_TYPE_FOLDER
                                ? SubscriptionCategory.Workspace
                                : SubscriptionCategory.WorkspaceDocument,
                          });
                        }}
                        onOperationStart={() => {
                          setBusy(item.id, "subscription");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>

                    <div className="flex flex-row">
                      <FavoriteButton
                        isHovering={true}
                        documentCategory={DocumentCategory.Workspace}
                        documentId={item.id}
                        title={item.name}
                        isFavorite={isFavorite}
                        workspaceId={workspaceId}
                        className="size-6 hover:bg-muted/80"
                        onOperationStart={() => {
                          setBusy(item.id, "favorite");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="mb-4 flex h-16 items-center">
                {isLink ? (
                  <Link2
                    className="size-12 text-muted-foreground"
                    aria-hidden="true"
                  />
                ) : (
                  <img
                    src={
                      getFileTypeIconSrc(
                        item.type === ITEM_TYPE_FOLDER
                          ? "folder"
                          : item.documentType
                      ) || ""
                    }
                    alt={item.documentType || ""}
                    className={cn("size-12")}
                  />
                )}
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <TruncatedTitle title={item.name} />
                {textMatch && (
                  <div>
                    <ContentMatchIndicator
                      count={textMatch.contentMatchCount}
                      indexStale={textMatch.searchIndexStale}
                    />
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate">
                    {isDocument
                      ? formatBytes(item.size || 0)
                      : isLink
                        ? getLinkHostname(item.url ?? "")
                        : `${item.fileCount || 0} items`}
                  </span>
                  <span>•</span>
                  <span>
                    {formatDate(item.updatedAt || item.createdAt || "")}
                  </span>
                  {item.documentSource && (
                    <>
                      <span>•</span>
                      <span>
                        {item.documentSource === UploadSource.LOCAL
                          ? "Lokal"
                          : item.documentSource}
                      </span>
                    </>
                  )}
                </div>
                {isDocument && indexing && (
                  // Own line, like the list view's: the footer has no room
                  // next to the owner's name.
                  <IndexingProgressLine view={indexing} className="truncate" />
                )}
                <div className="mt-auto pt-3 flex items-center justify-between border-t border-border/40">
                  <div className="flex items-center gap-2">
                    <UserAvatar user={documentOwner} avatarClassName="size-5" />
                    <span className="text-xs text-muted-foreground truncate">
                      {formatUsersName(documentOwner)}
                    </span>
                  </div>
                  {isDocument && (
                    <div className="flex items-center">
                      <IndexingStatusIcon
                        isIndexed={
                          item.revisions?.find(
                            (r) => r.revisionVersion === item.activeRevision
                          )?.isIndexed === true
                        }
                        searchIndexStale={isActiveRevisionSearchIndexStale(
                          item
                        )}
                        documentType={item.documentType}
                        isIndexing={indexing !== undefined}
                        indexingTooltip={
                          indexing && (
                            <IndexingProgressTooltip view={indexing} />
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {groupSubscriptionModalItem && (
        <ManageGroupSubscriptionModal
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              clearBusy(groupSubscriptionModalItem.itemId);
              setGroupSubscriptionModalItem(null);
            }
          }}
          variant="default"
          entityPath={groupSubscriptionModalItem.entityPath}
          entityName={groupSubscriptionModalItem.entityName}
          source="workspace"
          subscribeCategory={groupSubscriptionModalItem.subscribeCategory}
        />
      )}
    </>
  );
};

export default FileGrid;
